import { themeColors } from "../themeColors";
/**
 * @file  src\pages\AgentDashboard.js
 * @brief  This file is responsible for agent dashboard section
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Button from "react-bootstrap/Button";
import { Tabs, Tab, Badge } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Table from "react-bootstrap/Table";
import AddNewClientModal from "../components/MemberWantAd/AddNewClientModal";
import DeleteModal from "../components/Common/DeleteModal";
import { useNavigate } from "react-router-dom";
import { GenericTable } from "@/components";
import {
  getClientsList,
  saveAgentClient,
  deleteClient,
} from "../services/agentClientsService";
import EditClientModal from "../components/AgentDashboard/EditClientModal";
import ClientDeleteModal from "../components/AgentDashboard/ClientDeleteModal";
import TextBox from "../components/Common/TextBox";
import moment from "moment";
import { toast } from "react-toastify";
import {
  getLocalStorage,
  setLocalStorage,
  truncate,
  checkTutorialDisplayStatus,
  saveTutorialStatusToLocal,
  getUserType,
} from "../helpers/common";
import { pageURLs } from "@/constants";
import {
  CLIENT_DETAILS_KEY,
  PAGE_SIZE,
  USER_INFO_KEY,
  USER_TYPE_AGENT,
} from "../constants/common";
import JoyRide, { STATUS } from "react-joyride";
import DocumentationTutorial from "../components/Tutorial/DocumentationTutorial";
import AgentDashboardModal from "../components/Tutorial/AgentDashboardModal";
import { saveTutorialStatus } from "../services/commonServices";
import IntroductionTutorialIconStep1 from "../images/tut-quickview-icon.svg";
import IntroductionTutorialIconStep2 from "../images/tut-performing-icon.svg";
import IntroductionTutorialIconStep3 from "../images/tut-easilyadd-icon.svg";
import _ from "lodash";
import { PageWrapper } from "@/components/PageWrapper";
import { useMLSAgentAtGitchaHomeRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";


const tutorialSteps = [
  {
    target: ".tutorialRow",
    disableBeacon: true,
    placement: "left",
    title: "Quickly view all of your clients information",
    icon: IntroductionTutorialIconStep1,
    content:
      "You can view a summary here or select your client to view their full set of inventory and want listings.",
    hideCloseButton: true,
    showProgress: true,
    showSkipButton: true,
  },
  {
    target: ".tutorialRow",
    disableBeacon: true,
    placement: "top",
    title: "Figure out which listings / properties are performing",
    icon: IntroductionTutorialIconStep2,
    content:
      "This will help you modify your listings and inventory to figure out what strategies are worth replicating.",
    hideCloseButton: true,
    showProgress: true,
    showSkipButton: true,
  },
  {
    target: "#clientAdd",
    disableBeacon: true,
    placement: "left",
    title: "Easily add",
    icon: IntroductionTutorialIconStep3,
    content:
      "New clients, new inventory, and new want listings from one spot",
    hideCloseButton: true,
    showProgress: true,
    showSkipButton: true,
    spotlightPadding: false,
  },
]
//Tutorial tooltip
const TutorialToolTip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  size,
  isLastStep,
  skipProps,
}) => (
  <>
    <div {...tooltipProps}>
      <DocumentationTutorial
        continuous={continuous}
        index={index}
        step={step}
        backProps={backProps}
        skipProps={skipProps}
        primaryProps={primaryProps}
        isLastStep={isLastStep}
        size={size}
        finalStepBtn={"Done"}
      />
    </div>
  </>
);

const AgentMyClients = (props) => {
  const tutorialPage = 4; //Agent Dashboard
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [itemList, setList] = useState([]); // storing list
  const [searchValue, setSearchValue] = useState(null);
  const [clientAdded, setClientAdded] = useState(false);
  const [showNoResult, setShowNoResult] = useState(false);

  const [displayTutorial, setDisplayTutorial] = useState(false);
  const [tutorialModalShow, setTutorialModalShow] = useState(false);
  const [tutorialAccept, setTutorialAccept] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // column name for sorting userlisting
  const columns = [
    {
      accessor: "firstName",
      label: "Client",
      sorting: "Yes",
      cell: (c, i) => (
        <span className="txt-no-underline client-name-style">
          {truncate(`${c.FirstName} ${c.LastName}`, 15)}
        </span>
      ),
    },
    {
      accessor: "email",
      label: "Email",
      sorting: "Yes",
      cell: (c) => c.Email,
    },
    {
      accessor: "createdOn",
      label: "Date added",
      sorting: "Yes",
      cell: (c) =>
        c.CreatedOn ? moment(new Date(c.CreatedOn)).format("MMM Do, YYYY") : "",
    },
    {
      accessor: "inventory",
      label: "Inventory",
      sorting: "No",
      cell: (c, i) => (
        <span
          className={c.Inventory > 0 ? "table-badge-blue" : "table-badge-grey"}
        >
          {`${c.Inventory} stored`}
        </span>
      ),
    },
    {
      accessor: "wantAds",
      label: "Want Listings",
      sorting: "No",
      cell: (c) => (
        <span
          className={c.WantAds > 0 ? "table-badge-blue" : "table-badge-grey"}
        >
          {`${c.WantAds} active`}
        </span>
      ),
    },
    {
      accessor: "introSent",
      label: "Intros sent",
      sorting: "No",
      cell: (c) => c.IntroSent,
    },
    {
      accessor: "introReceived",
      label: "Intros received",
      sorting: "No",
      cell: (c) => c.IntroReceived,
    },
    {
      accessor: "actions",
      label: "",
      sorting: "No",
      cell: (c) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown className="d-inline-block align-middle more-icon-vertical">
            <Dropdown.Toggle id="dropdown-basic" variant="" align="end">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14 12.5C14 11.3954 13.1046 10.5 12 10.5C10.8954 10.5 10 11.3954 10 12.5C10 13.6046 10.8954 14.5 12 14.5C13.1046 14.5 14 13.6046 14 12.5Z"
                  fill={themeColors.neutral780}
                />
                <path
                  d="M14 7C14 5.89543 13.1046 5 12 5C10.8954 5 10 5.89543 10 7C10 8.10457 10.8954 9 12 9C13.1046 9 14 8.10457 14 7Z"
                  fill={themeColors.neutral780}
                />
                <path
                  d="M14 18C14 16.8954 13.1046 16 12 16C10.8954 16 10 16.8954 10 18C10 19.1046 10.8954 20 12 20C13.1046 20 14 19.1046 14 18Z"
                  fill={themeColors.neutral780}
                />
              </svg>
            </Dropdown.Toggle>
            <Dropdown.Menu className="dpdown-menu-style" align={"end"}>
              <Dropdown.Item
                onClick={() => {
                  setSelectedClient(c);
                  setShowEditModal(true);
                }}
              >
                <span style={{ marginRight: 8}}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.5581 2.05806C14.8021 1.81398 15.1979 1.81398 15.4419 2.05806L17.9419 4.55806C18.186 4.80214 18.186 5.19786 17.9419 5.44194L10.4419 12.9419C10.3247 13.0592 10.1658 13.125 10 13.125H7.5C7.15482 13.125 6.875 12.8452 6.875 12.5V10C6.875 9.83424 6.94085 9.67527 7.05806 9.55806L14.5581 2.05806ZM8.125 10.2589V11.875H9.74112L16.6161 5L15 3.38388L8.125 10.2589Z"
                      fill={themeColors.neutral780}
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12.6831 3.93306C12.9271 3.68898 13.3229 3.68898 13.5669 3.93306L16.0669 6.43306C16.311 6.67714 16.311 7.07286 16.0669 7.31694C15.8229 7.56102 15.4271 7.56102 15.1831 7.31694L12.6831 4.81694C12.439 4.57286 12.439 4.17714 12.6831 3.93306Z"
                      fill={themeColors.neutral780}
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M2.86612 2.86612C3.10054 2.6317 3.41848 2.5 3.75 2.5H10.625C10.9702 2.5 11.25 2.77982 11.25 3.125C11.25 3.47018 10.9702 3.75 10.625 3.75L3.75 3.75L3.75 16.25H16.25V9.375C16.25 9.02982 16.5298 8.75 16.875 8.75C17.2202 8.75 17.5 9.02982 17.5 9.375V16.25C17.5 16.5815 17.3683 16.8995 17.1339 17.1339C16.8995 17.3683 16.5815 17.5 16.25 17.5H3.75C3.41848 17.5 3.10054 17.3683 2.86612 17.1339C2.6317 16.8995 2.5 16.5815 2.5 16.25V3.75C2.5 3.41848 2.6317 3.10054 2.86612 2.86612Z"
                      fill={themeColors.neutral780}
                    />
                  </svg>
                </span>
                Edit Client
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  setSelectedClient(c);
                  setShowDeleteModal(true);
                }}
              >
                <span style={{ marginRight: 8 }}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.99979 1.6665C8.53799 1.6665 7.32079 2.75877 7.11649 4.1665H4.26574C4.23025 4.16043 4.19431 4.15744 4.15832 4.15755C4.12721 4.15822 4.0962 4.16122 4.06554 4.1665H2.70812C2.6253 4.16533 2.54307 4.18063 2.46622 4.21152C2.38936 4.2424 2.31941 4.28825 2.26043 4.3464C2.20145 4.40456 2.15462 4.47385 2.12265 4.55026C2.09068 4.62667 2.07422 4.70868 2.07422 4.7915C2.07422 4.87433 2.09068 4.95633 2.12265 5.03275C2.15462 5.10916 2.20145 5.17845 2.26043 5.2366C2.31941 5.29476 2.38936 5.34061 2.46622 5.37149C2.54307 5.40237 2.6253 5.41768 2.70812 5.4165H3.59923L4.64822 16.262C4.76117 17.4315 5.7547 18.3332 6.92931 18.3332H13.0694C14.2441 18.3332 15.2376 17.4316 15.3505 16.262L16.4003 5.4165H17.2915C17.3743 5.41768 17.4565 5.40237 17.5334 5.37149C17.6102 5.34061 17.6802 5.29476 17.7391 5.2366C17.7981 5.17845 17.845 5.10916 17.8769 5.03275C17.9089 4.95633 17.9254 4.87433 17.9254 4.7915C17.9254 4.70868 17.9089 4.62667 17.8769 4.55026C17.845 4.47385 17.7981 4.40456 17.7391 4.3464C17.6802 4.28825 17.6102 4.2424 17.5334 4.21152C17.4565 4.18063 17.3743 4.16533 17.2915 4.1665H15.9348C15.8685 4.15575 15.8009 4.15575 15.7346 4.1665H12.8831C12.6788 2.75877 11.4616 1.6665 9.99979 1.6665ZM9.99979 2.9165C10.7828 2.9165 11.4251 3.4449 11.6087 4.1665H8.3909C8.57447 3.4449 9.2168 2.9165 9.99979 2.9165ZM4.85412 5.4165H15.1446L14.1062 16.1416C14.0541 16.6812 13.6114 17.0832 13.0694 17.0832H6.92931C6.38809 17.0832 5.94458 16.6805 5.89253 16.1416L4.85412 5.4165ZM8.53169 7.49089C8.36607 7.49347 8.20825 7.56169 8.09289 7.68056C7.97754 7.79942 7.91408 7.95922 7.91645 8.12484V14.3748C7.91528 14.4577 7.93058 14.5399 7.96147 14.6167C7.99235 14.6936 8.0382 14.7635 8.09635 14.8225C8.15451 14.8815 8.2238 14.9283 8.30021 14.9603C8.37662 14.9923 8.45862 15.0087 8.54145 15.0087C8.62428 15.0087 8.70628 14.9923 8.78269 14.9603C8.85911 14.9283 8.9284 14.8815 8.98655 14.8225C9.04471 14.7635 9.09056 14.6936 9.12144 14.6167C9.15232 14.5399 9.16762 14.4577 9.16645 14.3748V8.12484C9.16765 8.04119 9.15205 7.95814 9.12056 7.88063C9.08908 7.80312 9.04235 7.73272 8.98316 7.6736C8.92396 7.61448 8.8535 7.56785 8.77595 7.53646C8.6984 7.50508 8.61534 7.48958 8.53169 7.49089ZM11.4484 7.49089C11.2827 7.49347 11.1249 7.56169 11.0096 7.68056C10.8942 7.79942 10.8307 7.95922 10.8331 8.12484V14.3748C10.8319 14.4577 10.8472 14.5399 10.8781 14.6167C10.909 14.6936 10.9549 14.7635 11.013 14.8225C11.0712 14.8815 11.1405 14.9283 11.2169 14.9603C11.2933 14.9923 11.3753 15.0087 11.4581 15.0087C11.5409 15.0087 11.623 14.9923 11.6994 14.9603C11.7758 14.9283 11.8451 14.8815 11.9032 14.8225C11.9614 14.7635 12.0072 14.6936 12.0381 14.6167C12.069 14.5399 12.0843 14.4577 12.0831 14.3748V8.12484C12.0843 8.04119 12.0687 7.95814 12.0372 7.88063C12.0057 7.80312 11.959 7.73272 11.8998 7.6736C11.8406 7.61448 11.7702 7.56785 11.6926 7.53646C11.6151 7.50508 11.532 7.48958 11.4484 7.49089Z"
                      fill={themeColors.neutral780}
                    />
                  </svg>
                </span>
                Delete Client
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
    },
  ];
  const [sort, setSort] = useState({ order: "desc", orderBy: "CreatedOn" });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  /**
   * To handle scroll
   */
  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
      document.documentElement.offsetHeight - 50 &&
      !loading &&
      hasMore
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [loading, hasMore]);

  useEffect(() => {
    const debouncedHandleScroll = _.debounce(handleScroll, 200);
    window.addEventListener("scroll", debouncedHandleScroll);
    return () => {
      window.removeEventListener("scroll", debouncedHandleScroll);
    };
  }, [handleScroll]);

  /**
   * function to call client list api
   * @param {*} data
   */
  const fetchData = useCallback(
    async (searchValue, sort) => {
      if (loading) return;
      setLoading(true);
      try {
        const data = {
          sortBy: sort.orderBy,
          search: searchValue,
          sortDir: sort.order,
          page: page,
          limit: PAGE_SIZE,
        };
        await getClientsList(data)
          .then((response) => {
            const responseResult = _.get(response, "result", null);
            if (response.errorCode === 0) {
              setShowNoResult(true);
              if (responseResult.length > 0) {
                setList([...itemList, ...responseResult]);
                if (checkTutorialDisplayStatus(tutorialPage)) {
                  setTutorialModalShow(true);
                }
              } else {
                if (itemList.length == 0) {
                  setTutorialModalShow(false);
                }
                setHasMore(false);
              }
            } else {
              setShowNoResult(false);
            }
          })
          .catch((err) => {
            console.log("Error", err);
          });
      } catch (error) {
        console.error("Failed to fetch items:", error);
      } finally {
        setLoading(false);
      }
    },
    [page],
  );

  useEffect(() => {
    fetchData(searchValue, sort);
  }, [fetchData, searchValue, clientAdded, sort]);

  /**
   * on searching the group
   * @param event any
   */
  const onSearch = (event) => {
    event.preventDefault();
    setSearchValue(event.target.value);
    setLoading(false);
    setPage(1);
    setList([]);
  };

  /**
   * To prevent the enter key page reloading
   * @param event any
   */
  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  /**
   * to handle sort columns
   * @param {string} accessor
   */
  const handleSort = (accessor) => {
    setSort((prevSort) => ({
      order:
        prevSort.order === "asc" && prevSort.orderBy === accessor
          ? "desc"
          : "asc",
      orderBy: accessor,
    }));

    setPage(1);
    setHasMore(true);
    setLoading(false);
    setList([]);
  };

  useEffect(() => {
    if (itemList.length > 0) {
      setShowNoResult(true);
    }
  }, [itemList]);

  /**
   * add client
   * @param {json} data
   */
  const addClientData = async (data) => {
    var data = {
      ...data,
    };
    setClientAdded(false);
    await saveAgentClient(data)
      .then((response) => {
        if (response.errorCode === 0) {
          const userInfo = getLocalStorage(USER_INFO_KEY);
          userInfo.userInfo["ClientCount"] = userInfo.userInfo?.ClientCount
            ? userInfo.userInfo?.ClientCount + 1
            : 1;
          setLocalStorage(USER_INFO_KEY, userInfo);

          if (response.result.agentExist) {
            toast.error(response.message);
          } else {
            toast.success("Client added successfully");
            setClientAdded(!clientAdded);
            setList([]);
            setShow(false);
            setPage(1);
            setHasMore(true);
            setLoading(false);
          }
        } else {
          return;
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const handleSaveClientDetails = async (reqPayload) => {
    await saveAgentClient(reqPayload)
      .then((response) => {
        if (response.errorCode === 0) {
          toast.success("Client updated.");
          setShowEditModal(false);
          setSelectedClient(null);
          setList([]);
          setPage(1);
          setHasMore(true);
          setLoading(false);
          setClientAdded(!clientAdded);
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const handleDeleteClient = async () => {
    const clientId = selectedClient?.ClientID;
    if (clientId) {
      await deleteClient(clientId)
        .then((response) => {
          if (response.errorCode === 0) {
            toast.success("Client deleted.");
            setShowDeleteModal(false);
            setSelectedClient(null);
            setList([]);
            setPage(1);
            setHasMore(true);
            setLoading(false);
            setClientAdded(!clientAdded);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  const emptyItemHtml = () => {
    if (!loading && showNoResult) {
      return (
        <div className="no-result-wrap no-result-padding">
          <h3>No results found</h3>
          <p>
            We couldn't find any listings that match your filters and search
            terms. Try a different search or reset all filters.
          </p>
        </div>
      );
    }
  };

  const handleClientClick = (clientId) => {
    setLocalStorage(CLIENT_DETAILS_KEY, { id: clientId });
    navigate(pageURLs.viewClientDetails);
  };

  let deleteButtonProps = {};

  //Tutorial status call back
  const handleJoyrideCallback = async (data) => {
    const { action, index, status, type, lifecycle } = data;
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) &&
      lifecycle == "complete"
    ) {
      setTutorialAccept(true);
      saveTutorialStatusToLocal(tutorialPage);
      await saveTutorialStatus(tutorialPage, 1); //Save status
      setDisplayTutorial(false);
    }
    if (action == "update" && index == 2 && status == "running") {
      document.querySelector("#dropdown-split-basic").click();
    }
  };

  const hideTutorialModal = async () => {
    setTutorialModalShow(false);
    saveTutorialStatusToLocal(tutorialPage);
    setTutorialAccept(true);
    await saveTutorialStatus(tutorialPage, 1); //Save status
  };

  const startTutorial = () => {
    setTutorialModalShow(false);
    setDisplayTutorial(true);
  };

  useMLSAgentAtGitchaHomeRedirect();

  return (
    <>
      {displayTutorial && itemList !== null && itemList?.length ? (
        <JoyRide
          steps={tutorialSteps}
          tooltipComponent={TutorialToolTip}
          continuous
          callback={handleJoyrideCallback}
        />
      ) : (
        ""
      )}

      <PageWrapper headerSidebarProps={{ tutorialAccept }}>
        <div className="m-0">
          <div className="main-content p-0 agent-no-login-content">
            <div className="agent-dashboard-wrap">
              <h2 className="mb-3">My Clients</h2>

              <div className="horizontal-tab-wrap">
                <div className="dashboard-top-bar">
                  <div className="dashboard-search">
                    <TextBox
                      type="text"
                      className="form-control"
                      placeholder="Search for client"
                      onChange={onSearch}
                      onKeyDown={onKeyDown}
                      value={searchValue ?? ""}
                    ></TextBox>
                    <span className="search-icon">search</span>
                  </div>
                  <Dropdown as={ButtonGroup} id="clientAdd">
                    <Button
                      variant="primary"
                      className="split-btn-margin text-nowrap ms-3 ms-sm-0"
                      onClick={handleShow}
                    >
                      Add Client
                    </Button>

                    <Dropdown.Toggle
                      split
                      variant="primary"
                      id="dropdown-split-basic"
                    />

                    <Dropdown.Menu isOpen={true}>
                      <Dropdown.Item
                        onClick={() => navigate(pageURLs.agentCreateInventory)}
                      >
                        <span>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 0.0898438C7.52138 0.0898438 7.04293 0.245532 6.64746 0.556966H6.64665L1.69141 4.4624C0.939922 5.05464 0.5 5.95958 0.5 6.91683V14.4535C0.5 15.2513 1.16044 15.9118 1.95833 15.9118H14.0417C14.8396 15.9118 15.5 15.2513 15.5 14.4535V6.91683C15.5 5.95958 15.0601 5.05464 14.3086 4.4624L14.25 4.4152V1.125C14.25 0.959245 14.1841 0.800283 14.0669 0.683077C13.9497 0.56587 13.7908 0.500017 13.625 0.5H11.5417C11.3759 0.500017 11.2169 0.56587 11.0997 0.683077C10.9825 0.800283 10.9167 0.959245 10.9167 1.125V1.78906L9.35254 0.556966C8.95707 0.245532 8.47862 0.0898438 8 0.0898438ZM8 1.33659C8.20408 1.33659 8.40823 1.40441 8.57943 1.53923L11.1551 3.56803C11.2473 3.64054 11.358 3.68566 11.4746 3.69824C11.5911 3.71082 11.7089 3.69035 11.8144 3.63917C11.9199 3.58798 12.0089 3.50815 12.0712 3.4088C12.1335 3.30945 12.1666 3.19458 12.1667 3.07731V1.75H13V4.71875C13.0001 4.81325 13.0216 4.9065 13.0629 4.9915C13.1042 5.0765 13.1642 5.15102 13.2384 5.20947L13.5355 5.44385C13.9873 5.79994 14.25 6.34158 14.25 6.91683V14.4535C14.25 14.5764 14.1646 14.6618 14.0417 14.6618H1.95833C1.8354 14.6618 1.75 14.5764 1.75 14.4535V6.91683C1.75 6.34158 2.01267 5.79994 2.46452 5.44385L7.42057 1.53923C7.59177 1.40441 7.79592 1.33659 8 1.33659ZM4.04167 7.99512C3.87591 7.99513 3.71695 8.06099 3.59974 8.17819C3.48254 8.2954 3.41668 8.45436 3.41667 8.62012V11.9535C3.41668 12.1192 3.48254 12.2782 3.59974 12.3954C3.71695 12.5126 3.87591 12.5784 4.04167 12.5785H6.54167C6.70742 12.5784 6.86638 12.5126 6.98359 12.3954C7.1008 12.2782 7.16665 12.1192 7.16667 11.9535V8.62012C7.16665 8.45436 7.1008 8.2954 6.98359 8.17819C6.86638 8.06099 6.70742 7.99513 6.54167 7.99512H4.04167ZM9.45833 7.99512C9.29258 7.99513 9.13362 8.06099 9.01641 8.17819C8.8992 8.2954 8.83335 8.45436 8.83333 8.62012V11.9535C8.83335 12.1192 8.8992 12.2782 9.01641 12.3954C9.13362 12.5126 9.29258 12.5784 9.45833 12.5785H11.9583C12.1241 12.5784 12.283 12.5126 12.4003 12.3954C12.5175 12.2782 12.5833 12.1192 12.5833 11.9535V8.62012C12.5833 8.45436 12.5175 8.2954 12.4003 8.17819C12.283 8.06099 12.1241 7.99513 11.9583 7.99512H9.45833ZM4.66667 9.24512H5.91667V11.3285H4.66667V9.24512ZM10.0833 9.24512H11.3333V11.3285H10.0833V9.24512Z"
                              fill={themeColors.neutral900}
                            />
                          </svg>
                        </span>
                        Add inventory
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() =>
                          navigate(pageURLs.agentCreatewantadMainScreen)
                        }
                      >
                        <span>
                          <svg
                            width="18"
                            height="16"
                            viewBox="0 0 18 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M1.26407 1.05319C1.49849 0.818769 1.81643 0.687073 2.14795 0.687073H15.8979C16.2295 0.687073 16.5474 0.818769 16.7818 1.05319C17.0163 1.28761 17.1479 1.60555 17.1479 1.93707V14.4371C17.1479 14.6537 17.0358 14.8549 16.8515 14.9687C16.6673 15.0826 16.4372 15.093 16.2434 14.9961L14.0229 13.8858L11.8025 14.9961C11.6265 15.0841 11.4194 15.0841 11.2434 14.9961L9.02295 13.8858L6.80246 14.9961C6.6265 15.0841 6.41939 15.0841 6.24344 14.9961L4.02295 13.8858L1.80246 14.9961C1.60871 15.093 1.37863 15.0826 1.19437 14.9687C1.01011 14.8549 0.897949 14.6537 0.897949 14.4371V1.93707C0.897949 1.60555 1.02965 1.28761 1.26407 1.05319ZM15.8979 1.93707L2.14795 1.93707L2.14795 13.4258L3.74344 12.6281C3.9194 12.5401 4.1265 12.5401 4.30246 12.6281L6.52295 13.7383L8.74344 12.6281C8.9194 12.5401 9.1265 12.5401 9.30246 12.6281L11.5229 13.7383L13.7434 12.6281C13.9194 12.5401 14.1265 12.5401 14.3025 12.6281L15.8979 13.4258V1.93707Z"
                              fill={themeColors.neutral780}
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M9.64795 6.31207C9.64795 5.9669 9.92777 5.68707 10.2729 5.68707H14.0229C14.3681 5.68707 14.6479 5.9669 14.6479 6.31207C14.6479 6.65725 14.3681 6.93707 14.0229 6.93707H10.2729C9.92777 6.93707 9.64795 6.65725 9.64795 6.31207Z"
                              fill={themeColors.neutral780}
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M9.64795 8.81207C9.64795 8.4669 9.92777 8.18707 10.2729 8.18707H14.0229C14.3681 8.18707 14.6479 8.4669 14.6479 8.81207C14.6479 9.15725 14.3681 9.43707 14.0229 9.43707H10.2729C9.92777 9.43707 9.64795 9.15725 9.64795 8.81207Z"
                              fill={themeColors.neutral780}
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M3.39795 5.06207C3.39795 4.7169 3.67777 4.43707 4.02295 4.43707H7.77295C8.11813 4.43707 8.39795 4.7169 8.39795 5.06207V10.0621C8.39795 10.4073 8.11813 10.6871 7.77295 10.6871H4.02295C3.67777 10.6871 3.39795 10.4073 3.39795 10.0621V5.06207ZM4.64795 5.68707V9.43707H7.14795V5.68707H4.64795Z"
                              fill={themeColors.neutral780}
                            />
                          </svg>
                        </span>
                        Post a want-listing
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                <div className="dashboard-table-wrap">
                  <div className="table-responsive">
                    {showNoResult && itemList?.length > 0 ? (
                      <GenericTable
                        data={itemList}
                        className="table"
                        onRowClick={(item) => handleClientClick(item.ClientID)}
                        renderHeader={() => (
                          <thead>
                            <tr>
                              {columns.map((col) => {
                                const sortClass =
                                  col.sorting === "Yes"
                                    ? col.accessor === sort.orderBy
                                      ? sort.order === "asc"
                                        ? "sorting_asc"
                                        : "sorting_desc"
                                      : "sorting"
                                    : "sorting-heading";
                                return (
                                  <th key={col.accessor}>
                                    {col.sorting === "Yes" ? (
                                      <span
                                        className={`${sortClass} first-head`}
                                        onClick={() => handleSort(col.accessor)}
                                      >
                                        {col.label}
                                      </span>
                                    ) : (
                                      <span className="sorting-heading">
                                        {col.label}
                                      </span>
                                    )}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                        )}
                        renderRow={(c, i) =>
                          columns.map((col, idx) => (
                            <td
                              key={idx}
                              className={
                                [
                                  "inventory",
                                  "wantAds",
                                  "introSent",
                                  "introReceived",
                                ].includes(col.accessor)
                                  ? i === 0
                                    ? "performingTutorialRow"
                                    : "TutorialRow"
                                  : i === 0
                                    ? "tutorialRow"
                                    : "clientListingRow"
                              }
                            >
                              {col.cell(c, i)}
                            </td>
                          ))
                        }
                      />
                    ) : !loading &&
                      showNoResult &&
                      (!searchValue || searchValue === "") &&
                      itemList?.length === 0 ? (
                      <div className="no-result-wrap no-result-padding">
                        <h3>You don’t have any clients</h3>
                        <p>
                          Use the Add Client button to create your first one!
                        </p>
                      </div>
                    ) : (
                      showNoResult && emptyItemHtml()
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>

      <AddNewClientModal
        show={show}
        onHide={handleClose}
        addClient={addClientData}
      />
      <EditClientModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedClient(null);
        }}
        handleSave={handleSaveClientDetails}
        clientDetails={selectedClient ? {
          FirstName: selectedClient.FirstName,
          LastName: selectedClient.LastName,
          Email: selectedClient.Email,
          ClientId: selectedClient.ClientID,
          AgentId: selectedClient.AgentID,
          Add_Info: selectedClient.Add_Info,
        } : null}
      />
      <ClientDeleteModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedClient(null);
        }}
        handleConfirm={handleDeleteClient}
        clientName={selectedClient ? `${selectedClient.FirstName} ${selectedClient.LastName}` : ""}
      />
      <AgentDashboardModal
        show={tutorialModalShow}
        onHide={() => hideTutorialModal()}
        title={_.get(deleteButtonProps, "title", "")}
        message={_.get(deleteButtonProps, "message", "")}
        buttonText={_.get(deleteButtonProps, "btnText", "")}
        handleConfirm={() => startTutorial()}
      />
    </>
  );
};

export default AgentMyClients;
